/**
 * Клиент событий исполнения (E8). Типы согласованы с сервером по ЗНАЧЕНИЯМ (клиент и api типы
 * напрямую не делят) — формы из apps/api/src/events/{kinds,store,service}.ts. Остаток и статусы
 * считает сервер (Р-2) и отдаёт готовыми. Путь относительный (`/api/...`): проксирует Caddy/Vite;
 * сессия — cookie HttpOnly (Р-5).
 *
 * Роли здесь нужны ТОЛЬКО чтобы не показывать заведомо запрещённую кнопку: настоящий запрет —
 * серверный (правило 5, контрольный пример 15).
 */

import type { Role } from "./roles";
import type { ApiResult } from "./contracts";
import type { Obligation, ObligationType } from "./obligations";

/** Вид события исполнения (ТЗ 4.2.1; значения = enum `ExecEventKind` сервера). */
export type ExecEventKind = "payment" | "shipment" | "acceptance" | "docs" | "other";

/** Наименования видов (термины ТЗ 4.2.1, правило 8). */
export const EVENT_KIND_LABELS: Record<ExecEventKind, string> = {
  payment: "Платёж",
  shipment: "Отгрузка / выполнение",
  acceptance: "Приёмка / УПД",
  docs: "Передача документа",
  other: "Иное",
};

/** Сегмент маршрута каждого вида: `POST /api/events/<path>` (совпадает с events/kinds.ts). */
const EVENT_KIND_PATH: Record<ExecEventKind, string> = {
  payment: "payments",
  shipment: "shipments",
  acceptance: "acceptances",
  docs: "documents",
  other: "other",
};

/**
 * Роли, которым вид события доступен (матрица 4.3): платёж — бухгалтерия/юрист/админ; остальные
 * виды — коммерческий/юрист/админ. Дубль серверной матрицы ради подсказки в интерфейсе.
 */
const EVENT_KIND_ROLES: Record<ExecEventKind, readonly Role[]> = {
  payment: ["accounting", "lawyer", "admin"],
  shipment: ["commercial", "lawyer", "admin"],
  acceptance: ["commercial", "lawyer", "admin"],
  docs: ["commercial", "lawyer", "admin"],
  other: ["commercial", "lawyer", "admin"],
};

/** Приложение А ТЗ: какие виды событий закрывают какой тип обязательства (совпадает с сервером). */
export const ALLOWED_EVENT_KINDS: Record<ObligationType, readonly ExecEventKind[]> = {
  supply: ["shipment"],
  works: ["shipment"],
  services: ["shipment"],
  payment_advance: ["payment"],
  payment_post: ["payment"],
  acceptance: ["acceptance"],
  docs: ["docs"],
  guarantee: ["other"],
  security: ["payment", "docs"],
  ret: ["other"],
  other: ["other"],
};

/** Может ли пользователь зарегистрировать событие этого вида (подсказка UI; запрет — на сервере). */
export function canRegisterKind(roles: readonly Role[], kind: ExecEventKind): boolean {
  return roles.some((r) => EVENT_KIND_ROLES[kind].includes(r));
}

/** Виды событий, доступные пользователю для данного обязательства (пересечение Прил. А и прав). */
export function availableKinds(
  roles: readonly Role[],
  type: ObligationType,
): readonly ExecEventKind[] {
  return ALLOWED_EVENT_KINDS[type].filter((k) => canRegisterKind(roles, k));
}

/** Событие исполнения — ответ сервера. Деньги/даты строками (правила 2 и 3). */
export interface ExecEvent {
  readonly id: string;
  readonly obligationId: string;
  readonly kind: ExecEventKind;
  readonly date: string;
  readonly amount: string | null;
  readonly docRef: string | null;
  readonly note: string | null;
  readonly isFull: boolean | null;
  readonly authorId: string;
  readonly authorName: string;
  readonly createdAt: string;
  /** Сторно (E8a): id исправляемого события; null — обычное событие. */
  readonly reversesEventId: string | null;
  /** Причина сторнирования — заполнена только у записи сторно. */
  readonly reason: string | null;
  /** id записи сторно, отменившей это событие; null — событие действует. */
  readonly reversedByEventId: string | null;
  /**
   * Доступно ли сторнирование текущему пользователю ПРЯМО СЕЙЧАС (правило В19). Считает сервер:
   * ни часовой пояс организации, ни матрицу прав клиенту знать не нужно, а настоящий запрет всё
   * равно стоит на маршруте. Приходит только в ленте событий.
   */
  readonly canReverse?: boolean;
}

/** Поля формы события. Пустая строка в необязательном поле = «нет значения». */
export interface ExecEventInput {
  readonly obligationId: string;
  readonly date: string;
  readonly amount?: string;
  readonly docRef?: string;
  readonly note?: string;
  /** «Исполнено полностью» — только для неденежных обязательств (у денежных считает сервер). */
  readonly isFull?: boolean;
}

/** Итог регистрации: событие + пересчитанные обязательства (своё и зависимые). */
export interface EventRegistered {
  readonly event: ExecEvent;
  readonly obligation: Obligation;
  readonly dependents: Obligation[];
  readonly closed: boolean;
}

/** Итог сторнирования (E8a): запись сторно, исходное событие и пересчитанные обязательства. */
export interface EventReversed {
  readonly reversal: ExecEvent;
  readonly original: ExecEvent;
  readonly obligation: Obligation;
  readonly dependents: Obligation[];
  /** Переоткрылось ли обязательство (было исполнено — стало нет). */
  readonly reopened: boolean;
  /**
   * Прежняя дата исполнения, если она СДВИНУЛАСЬ, а обязательство осталось закрытым; иначе null.
   * От этой даты считаются неустойка и проценты (ст. 395), поэтому сдвиг проговаривается вслух.
   */
  readonly doneDateShiftedFrom: string | null;
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403) {
    return {
      ok: false,
      reason: "forbidden",
      // Сообщение сервера точнее: отказ бывает и по матрице 4.3 (не та роль), и по правилу В19
      // (чужое или вчерашнее событие). Свой текст — только когда сервер его не прислал.
      message: message ?? "Событие этого вида вносит другая роль.",
    };
  }
  if (res.status === 409)
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  if (res.status === 404)
    return { ok: false, reason: "network", message: message ?? "Обязательство не найдено." };
  if (res.status === 400)
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/** Лента событий обязательства (GET /api/obligations/:id/events) — просмотр доступен всем ролям. */
export async function fetchEvents(obligationId: string): Promise<ApiResult<ExecEvent[]>> {
  let res: Response;
  try {
    res = await fetch(`/api/obligations/${encodeURIComponent(obligationId)}/events`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { events?: ExecEvent[] } | null;
  return { ok: true, value: body?.events ?? [] };
}

/** Зарегистрировать событие вида `kind` (POST /api/events/<вид>). */
export async function registerEvent(
  kind: ExecEventKind,
  input: ExecEventInput,
): Promise<ApiResult<EventRegistered>> {
  let res: Response;
  try {
    res = await fetch(`/api/events/${EVENT_KIND_PATH[kind]}`, {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as EventRegistered | null;
  if (!body?.event) return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body };
}

/**
 * Сторнировать ошибочное событие (POST /api/events/<вид>/:id/reverse, E8a).
 * Кто и когда вправе — решает сервер (правило В19); подсказка в ленте — поле `canReverse`.
 */
export async function reverseEvent(
  kind: ExecEventKind,
  eventId: string,
  reason: string,
): Promise<ApiResult<EventReversed>> {
  let res: Response;
  try {
    res = await fetch(
      `/api/events/${EVENT_KIND_PATH[kind]}/${encodeURIComponent(eventId)}/reverse`,
      {
        method: "POST",
        headers: JSON_HEADERS,
        credentials: "same-origin",
        body: JSON.stringify({ reason }),
      },
    );
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as EventReversed | null;
  if (!body?.reversal) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body };
}
